/**
 * OPS-003 · Operations Attention Centre.
 *
 * Single page answering "what needs attention now?" over the DEV-016
 * exceptions API. Zero rule logic lives here — the UI is a projection
 * of `/api/operations/rules/exceptions/{search,summary,{id}}` and
 * navigates operators to the rule-supplied `resolution_route`.
 *
 * OPS-003A · Corrective pass (2026-02-21)
 * -----------------------------------------------------------------
 *   • Wrapped in <OperationsShell> so the sidebar stays reachable.
 *   • Home button (`attn-home`) navigates back to /operations.
 *   • EVERY URL mutation (filters, paging, reset, detail open/close)
 *     uses `replace: true`. One browser Back leaves the Attention
 *     Centre entirely, no matter how many interactions preceded it.
 *   • Default status is "active" both visually (select shows Active)
 *     and functionally (server default remains "active"; the URL is
 *     backfilled on first mount).
 *   • Row + detail obey the canonical project-identity fallback:
 *         reference → title/name → address → "Project reference unavailable"
 *     A raw project UUID is NEVER used as the primary display.
 *   • Full filter surface: Status, Severity, Effect, Sort, Rule,
 *     Category, Responsible role, Responsible person, Customer,
 *     Project, Min age — every one URL-backed, compoundable,
 *     resettable and refreshable.
 *   • "Blocking" tile removed. See BLOCKING_TILE_REMOVAL comment.
 *   • "Assigned to me" uses the fleet-wide `assigned_to_me` count
 *     from `/exceptions/summary` (not filtered by the visible page).
 *
 * BLOCKING_TILE_REMOVAL
 * ----------------------
 * The canonical DEV-016 rules engine has exactly one rule with
 * `effect=BLOCKING` (`installation_requires_approved_survey`) and it
 * is a PREREQUISITE rule gated on `ctx.proposed_action` — it only
 * fires via `evaluate_prerequisites(persist=False)` inside the
 * `hooks.safe_prereq` gate ahead of a candidate mutation.
 *
 * Consequences (verified live against `db.rule_exceptions`):
 *   • No blocking-effect exception is ever upserted into the
 *     `rule_exceptions` collection.
 *   • `summary.by_effect` therefore never carries a `blocking` bucket.
 *
 * "Blocking" is a transient decision artefact (409 response + audit
 * row), not a persisted active exception. Rendering an empty
 * always-zero tile misled operators. Per OPS-003A guidance, the card
 * is removed rather than conditionally hidden.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    AlertOctagon, AlertTriangle, Bell, Info, RefreshCw, X, Loader2,
    ArrowUpRight, ChevronRight, Filter as FilterIcon, Home,
} from "lucide-react";
import OperationsShell from "@/components/operations/OperationsShell";
import PortalHero from "@/components/ui/PortalHero";
import { portalIdentity } from "@/lib/portalIdentity";
import { api, formatApiError } from "@/lib/api";

// ── Canonical filter registry (URL as single source of truth) ─────
const CANONICAL_FILTERS = [
    "status", "severity", "effect", "rule_key", "rule_category",
    "responsible_role", "responsible_user_id",
    "project_id", "customer_id", "min_age_hours", "sort",
    // OPS-003B · assignment / acknowledgement filters.
    "assignment_status", "acknowledgement_status",
    "assigned_user_id", "assigned_role",
];
const VALID_SORT = new Set(["severity_desc", "age_desc", "detected_desc"]);
const VALID_STATUS = new Set(["active", "resolved", "all"]);
const VALID_SEVERITY = new Set(["critical", "high", "medium", "low", "information"]);
const VALID_EFFECT = new Set(["attention", "warning", "informational"]);
const VALID_CATEGORY = new Set(["prerequisite", "dependency", "successor", "stalled"]);
const VALID_ASSIGNMENT = new Set(["assigned", "unassigned"]);
const VALID_ACK = new Set(["acknowledged", "unacknowledged"]);
const PAGE_SIZE = 25;

// Canonical project-identity fallback chain. A raw project UUID must
// NEVER be used as the primary displayed identity.
function projectDisplay(row) {
    return (
        row.project_reference ||
        row.project_title ||
        row.project_address ||
        "Project reference unavailable"
    );
}

// Canonical responsible-person display. When the backing user has been
// deleted (`responsible_user_name` is null but `responsible_user_id`
// still points somewhere), we surface a neutral "Former or unavailable
// user" label — never a shortened raw ID. Falls through to the role
// label when no user is assigned at all.
//
// OPS-003B · When a MANUAL assignment exists it TAKES PRECEDENCE over
// the rule-derived responsible_* fields (we display who owns the
// exception right now, not who the rule engine suggested).
function personDisplay(row) {
    if (row.assigned_user_id) {
        return row.assigned_user_current_name
            || row.assigned_user_name
            || "Former or unavailable user";
    }
    if (row.assigned_role) return row.assigned_role;
    if (row.responsible_user_name) return row.responsible_user_name;
    if (row.responsible_user_id) return "Former or unavailable user";
    return row.responsible_role || "—";
}

// OPS-003B · resolution-route sanitiser. Only allow same-origin
// internal paths — never a `javascript:` / `data:` scheme or a
// protocol-relative URL. If the input is malformed we surface `null`
// so the button is hidden rather than exploded.
function safeInternalRoute(raw) {
    if (!raw || typeof raw !== "string") return null;
    const s = raw.trim();
    if (!s) return null;
    if (s.startsWith("//") || /^[a-z][a-z0-9+.-]*:/i.test(s)) {
        // Only http(s) URLs matching the current origin are allowed.
        if (/^https?:\/\//i.test(s)) {
            try {
                const url = new URL(s);
                const host = (typeof window !== "undefined"
                    && window.location && window.location.host) || "";
                if (host && url.host.toLowerCase() !== host.toLowerCase()) return null;
                return url.pathname + (url.search || "") + (url.hash || "");
            } catch { return null; }
        }
        return null;
    }
    if (!s.startsWith("/")) return null;
    return s;
}

// ── Severity visual language (colour + icon + text) ───────────────
const SEV_STYLE = {
    critical:    { chip: "bg-[#DC2626]/10 text-[#B91C1C] border-[#DC2626]/25", dot: "bg-[#DC2626]", icon: AlertOctagon, label: "Critical" },
    high:        { chip: "bg-[#F59E0B]/10 text-[#B45309] border-[#F59E0B]/25", dot: "bg-[#F59E0B]", icon: AlertTriangle, label: "High" },
    medium:      { chip: "bg-[#EAB308]/10 text-[#854D0E] border-[#EAB308]/25", dot: "bg-[#EAB308]", icon: Bell, label: "Medium" },
    low:         { chip: "bg-[#0EA5E9]/10 text-[#0369A1] border-[#0EA5E9]/25", dot: "bg-[#0EA5E9]", icon: Info, label: "Low" },
    information: { chip: "bg-[#94A3B8]/10 text-[#334155] border-[#94A3B8]/25", dot: "bg-[#94A3B8]", icon: Info, label: "Info" },
};
const EFFECT_LABEL = {
    blocking: "Blocking", warning: "Warning",
    attention: "Attention", informational: "Informational",
};

// ── Age helpers (kept UI-side; API returns first_seen_at) ────────
function ageHours(iso) {
    if (!iso) return null;
    const t = Date.parse(iso);
    if (Number.isNaN(t)) return null;
    return (Date.now() - t) / 3_600_000;
}
function humanAge(iso) {
    const h = ageHours(iso);
    if (h == null) return "—";
    if (h < 1) return `${Math.max(1, Math.round(h * 60))}m`;
    if (h < 24) return `${Math.round(h)}h`;
    return `${Math.round(h / 24)}d`;
}

// ── URL <-> filter helpers ────────────────────────────────────────
function readFilters(searchParams) {
    const raw = {};
    for (const k of CANONICAL_FILTERS) raw[k] = searchParams.get(k) || null;
    if (raw.status && !VALID_STATUS.has(raw.status)) raw.status = null;
    if (raw.severity && !VALID_SEVERITY.has(raw.severity)) raw.severity = null;
    if (raw.effect && !VALID_EFFECT.has(raw.effect)) raw.effect = null;
    if (raw.sort && !VALID_SORT.has(raw.sort)) raw.sort = null;
    if (raw.rule_category && !VALID_CATEGORY.has(raw.rule_category)) raw.rule_category = null;
    if (raw.assignment_status && !VALID_ASSIGNMENT.has(raw.assignment_status)) raw.assignment_status = null;
    if (raw.acknowledgement_status && !VALID_ACK.has(raw.acknowledgement_status)) raw.acknowledgement_status = null;
    if (raw.min_age_hours != null && Number.isNaN(Number(raw.min_age_hours))) raw.min_age_hours = null;
    return raw;
}
function effectiveFilters(f) {
    // Server defaults: status=active, sort=severity_desc.
    return { ...f, status: f.status || "active", sort: f.sort || "severity_desc" };
}

function ExceptionRow({ row, onOpen, selected }) {
    const sev = SEV_STYLE[row.severity] || SEV_STYLE.information;
    const Icon = sev.icon;
    const customer = row.customer_name || "Unknown customer";
    const project = projectDisplay(row);
    const isAssigned = Boolean(row.assigned_user_id || row.assigned_role);
    const isAcknowledged = Boolean(row.acknowledged_at);
    const noteCount = Number(row.note_count || 0);
    const lastActionIso = row.last_action_at || row.last_seen_at;
    return (
        <button
            data-testid={`attn-row-${row.id}`}
            onClick={() => onOpen(row.id)}
            className={`w-full text-left px-4 py-3 border-b border-[#E5E7EB] transition-colors ${
                selected ? "bg-[#EEF2FF]" : "hover:bg-[#F8FAFC]"
            }`}
            aria-current={selected ? "true" : undefined}
        >
            <div className="flex items-start gap-3">
                <div className={`shrink-0 w-1.5 self-stretch rounded ${sev.dot}`} aria-hidden="true" />
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 border rounded-full text-[11px] font-medium ${sev.chip}`}>
                            <Icon size={12} strokeWidth={2} aria-hidden="true" />
                            <span className="sr-only">Severity:</span>{sev.label}
                        </span>
                        <span className="text-[11px] text-[#4B5565]">
                            <span className="sr-only">Effect:</span>{EFFECT_LABEL[row.effect] || row.effect}
                        </span>
                        {/* OPS-003B assignment badge */}
                        <span
                            data-testid={`attn-row-${row.id}-assignment`}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${
                                isAssigned
                                    ? "border-[#3E9E2E]/30 bg-[#3E9E2E]/10 text-[#357F26]"
                                    : "border-[#94A3B8]/30 bg-[#F1F5F9] text-[#4B5565]"
                            }`}
                            title={isAssigned ? "Assigned" : "Unassigned"}
                        >
                            {isAssigned ? "Assigned" : "Unassigned"}
                        </span>
                        {/* OPS-003B ack indicator */}
                        <span
                            data-testid={`attn-row-${row.id}-ack`}
                            className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border ${
                                isAcknowledged
                                    ? "border-[#0EA5E9]/30 bg-[#0EA5E9]/10 text-[#0369A1]"
                                    : "border-[#F59E0B]/30 bg-[#F59E0B]/10 text-[#B45309]"
                            }`}
                            title={isAcknowledged ? "Acknowledged" : "Awaiting acknowledgement"}
                        >
                            {isAcknowledged ? "Acknowledged" : "Unack’d"}
                        </span>
                        {noteCount > 0 && (
                            <span
                                data-testid={`attn-row-${row.id}-notes`}
                                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium border border-[#E5E7EB] bg-white text-[#4B5565]"
                                title={`${noteCount} note${noteCount === 1 ? "" : "s"}`}
                            >
                                {noteCount} note{noteCount === 1 ? "" : "s"}
                            </span>
                        )}
                        {row.status === "resolved" && (
                            <span data-testid={`attn-row-${row.id}-resolved`} className="text-[11px] text-[#3E9E2E]">✓ Resolved</span>
                        )}
                        <span className="text-[11px] text-[#94A3B8] ml-auto" data-testid={`attn-row-${row.id}-age`}>
                            <span className="sr-only">Age:</span>{humanAge(row.first_seen_at)}
                        </span>
                    </div>
                    <div className="mt-1 text-sm font-medium text-[#0F172A] truncate" title={row.required_action}>
                        {row.required_action || row.rule_key}
                    </div>
                    <div className="mt-0.5 text-[12px] text-[#4B5565] truncate">
                        <span data-testid={`attn-row-${row.id}-customer`}>{customer}</span>
                        <span className="mx-1">·</span>
                        <span data-testid={`attn-row-${row.id}-project`}>{project}</span>
                        <span className="mx-1">·</span>
                        <span data-testid={`attn-row-${row.id}-person`}>{personDisplay(row)}</span>
                        <span className="mx-1">·</span>
                        <span data-testid={`attn-row-${row.id}-last-action`} className="text-[#94A3B8]">
                            last action {humanAge(lastActionIso)}
                        </span>
                    </div>
                </div>
                <ChevronRight size={16} className="text-[#94A3B8]" aria-hidden="true" />
            </div>
        </button>
    );
}

function DetailPanel({ exceptionId, onClose, onMutated }) {
    const [row, setRow] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [assignees, setAssignees] = useState({ users: [], roles: [] });
    const navigate = useNavigate();

    // OPS-003B1 · reload MUST return a Promise so child components
    // can await it before clearing their local input state. Fixes
    // the "assignment persisted but Ownership stayed Unassigned
    // until another action refreshed it" defect.
    const reload = useCallback(async () => {
        if (!exceptionId) return null;
        setLoading(true); setError(null);
        try {
            const fresh = await api.operationsExceptionDetail(exceptionId);
            setRow(fresh);
            return fresh;
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail));
            return null;
        } finally {
            setLoading(false);
        }
    }, [exceptionId]);

    useEffect(() => { reload(); }, [reload]);
    useEffect(() => {
        // Load the assignable roster once per detail open — cheap
        // enough (Operations-only) and never leaks stale caches.
        api.operationsAttentionAssignees()
            .then((r) => setAssignees({ users: r?.users || [], roles: r?.roles || [] }))
            .catch(() => {});
    }, [exceptionId]);

    // OPS-003B1 · Structured resolution descriptor lives on the row
    // itself (server-computed + shape-validated). Falls back to the
    // legacy string field only when the server returned no descriptor
    // — but only if that legacy value passes `safeInternalRoute`. If
    // both are missing/invalid, the "Fix underlying issue" button is
    // HIDDEN — we never route the operator to a dashboard fallback.
    const resolutionDescriptor = row?.resolution || null;
    const resolvedRoute = useMemo(() => {
        if (!row) return null;
        if (resolutionDescriptor?.route) {
            return safeInternalRoute(resolutionDescriptor.route);
        }
        return safeInternalRoute(row.resolution_route);
    }, [row, resolutionDescriptor]);
    const isResolved = row?.status === "resolved";
    const resolveActionLabel = isResolved
        ? "View related record"
        : (resolutionDescriptor?.label || "Fix underlying issue");

    // OPS-003B1 · child mutations await this so their local input
    // state stays consistent with the freshly-refetched row.
    const notifyMutated = useCallback(async () => {
        const fresh = await reload();
        if (onMutated) onMutated();
        return fresh;
    }, [reload, onMutated]);

    if (!exceptionId) return null;

    return (
        <aside
            data-testid="attn-detail-panel"
            className="fixed inset-y-0 right-0 w-full sm:w-[520px] bg-white border-l border-[#E5E7EB] z-30 flex flex-col shadow-xl"
            role="dialog" aria-label="Exception detail"
        >
            <div className="flex items-center gap-2 px-4 py-3 border-b border-[#E5E7EB]">
                <h2 className="text-sm font-semibold text-[#0F172A] flex-1">Exception detail</h2>
                <button
                    data-testid="attn-detail-close"
                    onClick={onClose}
                    className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-[#F1F5F9]"
                    aria-label="Close detail panel"
                ><X size={16} /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
                {loading && !row && (
                    <div className="flex items-center gap-2 text-sm text-[#4B5565]">
                        <Loader2 className="animate-spin" size={16} /> Loading…
                    </div>
                )}
                {error && <div data-testid="attn-detail-error" className="text-sm text-[#DC2626]">{error}</div>}
                {row && (
                    <div className="space-y-4 text-sm">
                        <div>
                            <div className="text-[11px] uppercase tracking-wide text-[#94A3B8]">Issue</div>
                            <div className="mt-1 font-medium text-[#0F172A]" data-testid="attn-detail-issue">{row.rule_metadata?.name || row.rule_key}</div>
                            {row.rule_metadata?.description && (
                                <div className="mt-1 text-[#4B5565]">{row.rule_metadata.description}</div>
                            )}
                            {isResolved && (
                                <div data-testid="attn-detail-resolved-banner"
                                     className="mt-2 text-[12px] text-[#3E9E2E] bg-[#3E9E2E]/[0.08] border border-[#3E9E2E]/25 rounded-md px-2 py-1">
                                    Resolved history — read-only. Prior assignment, acknowledgement and notes are preserved.
                                </div>
                            )}
                        </div>

                        {/* OPS-003B1 · Clearly separate exception management
                            from issue resolution. */}
                        <div data-testid="attn-detail-scope-note"
                             className="text-[11px] text-[#4B5565] italic px-2 py-1 border-l-2 border-[#E5E7EB]">
                            Assignment, acknowledgement and notes track operational handling.
                            The exception resolves only when the underlying issue is corrected.
                        </div>

                        <OwnershipCard
                            row={row}
                            assignees={assignees}
                            onMutated={notifyMutated}
                            readOnly={isResolved}
                        />

                        <AcknowledgementCard
                            row={row}
                            onMutated={notifyMutated}
                            readOnly={isResolved}
                        />

                        <div className="grid grid-cols-2 gap-3">
                            <Meta k="Severity" v={SEV_STYLE[row.severity]?.label || row.severity} />
                            <Meta k="Effect" v={EFFECT_LABEL[row.effect] || row.effect} />
                            <Meta k="Status" v={row.status} />
                            <Meta k="Occurrences" v={row.occurrence_count} />
                            <Meta k="First seen" v={row.first_seen_at?.slice(0, 19).replace("T", " ")} />
                            <Meta k="Last seen" v={row.last_seen_at?.slice(0, 19).replace("T", " ")} />
                            {row.resolved_at && <Meta k="Resolved" v={row.resolved_at.slice(0, 19).replace("T", " ")} />}
                            <Meta k="Age" v={humanAge(row.first_seen_at)} />
                        </div>
                        <Meta k="Failed prerequisite / dependency" v={row.dependency_failed || "—"} />
                        <Meta k="Affected entity" v={row.affected_entity_type ? `${row.affected_entity_type} · ${row.affected_entity_id || "—"}` : "project"} />
                        <Meta k="Project" v={projectDisplay(row)} />
                        {row.project_address && row.project_address !== projectDisplay(row) && (
                            <Meta k="Site address" v={row.project_address} />
                        )}
                        <Meta k="Customer" v={row.customer_name || "—"} />
                        <Meta k="Required action" v={row.required_action || "—"} />

                        {row.resolution_history?.length > 0 && (
                            <div>
                                <div className="text-[11px] uppercase tracking-wide text-[#94A3B8]">Recurrence history</div>
                                <ul className="mt-1 space-y-1" data-testid="attn-detail-recurrence">
                                    {row.resolution_history.map((h, i) => (
                                        <li key={i} className="text-[12px] text-[#4B5565]">
                                            resolved {h.resolved_at?.slice(0, 16).replace("T", " ")} → reactivated {h.reactivated_at?.slice(0, 16).replace("T", " ")}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                        {row.evidence && Object.keys(row.evidence).length > 0 && (
                            <details className="text-[12px]" data-testid="attn-detail-evidence">
                                <summary className="cursor-pointer text-[#0F172A] font-medium">Supporting evidence</summary>
                                <ul className="mt-1 space-y-0.5">
                                    {Object.entries(row.evidence).map(([k, v]) => (
                                        <li key={k}><span className="text-[#94A3B8]">{k}:</span> <span className="text-[#0F172A]">{String(v)}</span></li>
                                    ))}
                                </ul>
                            </details>
                        )}
                        <Meta k="Rule" v={`${row.rule_key} v${row.rule_version} · ${row.rule_metadata?.category || "—"}`} />

                        <NotesSection
                            row={row}
                            onMutated={notifyMutated}
                            readOnly={isResolved}
                        />

                        <ActionHistorySection row={row} />

                        {/* OPS-003B1 · Explicit "Fix underlying issue"
                            block. Hidden when no valid resolution
                            destination exists — we never route the
                            operator to a dashboard fallback. */}
                        <div className="border border-[#E5E7EB] rounded-md p-3 bg-white">
                            <div className="text-[11px] uppercase tracking-wide text-[#94A3B8]">
                                Fix underlying issue
                            </div>
                            <div className="text-[12px] text-[#4B5565] mt-1">
                                Opens the underlying business record where this rule can be corrected.
                                {isResolved && " This exception is already resolved — the button opens the record for review only."}
                            </div>
                            <div className="mt-2 flex gap-2 items-center">
                                {resolvedRoute ? (
                                    <button
                                        data-testid="attn-detail-resolve-btn"
                                        onClick={() => navigate(resolvedRoute)}
                                        className={`flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-md text-sm font-medium ${
                                            isResolved
                                                ? "bg-white text-[#0F172A] border border-[#0F172A] hover:bg-[#F8FAFC]"
                                                : "bg-[#3E9E2E] text-white hover:bg-[#357F26]"
                                        }`}
                                        title={resolutionDescriptor?.target_type
                                            ? `Target: ${resolutionDescriptor.target_type}`
                                            : "Open the screen where this rule can be resolved"}
                                    >
                                        {resolveActionLabel} <ArrowUpRight size={14} />
                                    </button>
                                ) : (
                                    <div data-testid="attn-detail-resolve-unavailable"
                                         className="flex-1 text-[12px] text-[#94A3B8] italic px-4 py-2">
                                        No resolution screen available for this exception.
                                    </div>
                                )}
                                {!isResolved && (
                                    <ReevaluateButton
                                        exceptionId={row.id}
                                        onMutated={notifyMutated}
                                    />
                                )}
                                {isResolved && (
                                    <ReevaluateButton
                                        exceptionId={row.id}
                                        onMutated={notifyMutated}
                                        recheckLabel
                                    />
                                )}
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </aside>
    );
}

function ReevaluateButton({ exceptionId, onMutated, recheckLabel = false }) {
    const [busy, setBusy] = useState(false);
    const [msg, setMsg] = useState(null);
    async function onClick() {
        setBusy(true); setMsg(null);
        try {
            const r = await api.operationsAttentionReevaluate(exceptionId);
            await onMutated();
            // OPS-003B1 · Explicit outcome messages — never claim
            // "rule now passes" if the evaluator emitted a result at
            // a different dedup key (dedup_key_drift).
            const map = {
                resolved: "Exception resolved — underlying condition now passes.",
                reopened: "Exception reopened — condition failed again.",
                condition_still_active: "Condition still active — nothing changed.",
                dedup_key_drift:
                    "The rule evaluated the project but produced no matching result for this exception. Reseed the data using canonical entity references or open the related record to correct the condition.",
            };
            setMsg(map[r.outcome] || `Outcome: ${r.outcome}`);
        } catch (e) {
            setMsg(formatApiError(e?.response?.data?.detail) || "Re-evaluation failed.");
        } finally {
            setBusy(false);
            setTimeout(() => setMsg(null), 6000);
        }
    }
    return (
        <div className="flex flex-col items-stretch">
            <button
                data-testid="attn-detail-reevaluate"
                onClick={onClick}
                disabled={busy}
                className="inline-flex items-center justify-center gap-1 px-3 py-2.5 rounded-md border border-[#E5E7EB] bg-white text-[13px] text-[#0F172A] hover:bg-[#F8FAFC] disabled:opacity-50"
                title={recheckLabel ? "Re-check the underlying rule condition" : "Re-run the underlying rule now"}
            >
                <RefreshCw size={14} className={busy ? "animate-spin" : ""} /> {recheckLabel ? "Re-check condition" : "Re-evaluate"}
            </button>
            {msg && <div data-testid="attn-detail-reevaluate-msg" className="text-[11px] text-[#4B5565] mt-1 max-w-[220px]">{msg}</div>}
        </div>
    );
}

function OwnershipCard({ row, assignees, onMutated, readOnly = false }) {
    const [mode, setMode] = useState("user");   // user | role
    const [userId, setUserId] = useState("");
    const [role, setRole] = useState("");
    const [reason, setReason] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);

    const currentOwner = row.assigned_user_id
        ? (row.assigned_user_current_name || row.assigned_user_name || "Former or unavailable user")
        : (row.assigned_role || null);
    const hasOwner = Boolean(row.assigned_user_id || row.assigned_role);
    const needsReason = hasOwner;

    async function submit(kind) {
        setError(null);
        if (needsReason && !reason.trim()) {
            setError("A reason is required when changing an existing owner.");
            return;
        }
        setBusy(true);
        try {
            const body = {
                assignee_type: kind,
                expected_version: row.assignment_version || 0,
                reason: reason.trim() || null,
            };
            if (kind === "user") body.user_id = userId;
            if (kind === "role") body.role = role;
            await api.operationsAttentionAssign(row.id, body);
            // OPS-003B1 · await the refetch BEFORE we clear our
            // local input state, so the operator never sees a
            // reset form beside a stale "Unassigned" label.
            await onMutated();
            setReason(""); setUserId(""); setRole("");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = detail && detail.error_code;
            const msg = detail && (detail.detail || (typeof detail === "string" ? detail : null));
            if (code === "version_conflict") {
                setError("This exception was updated by someone else — refresh and try again.");
            } else if (code === "exception_resolved") {
                setError("This exception is already resolved — assignment is disabled.");
            } else {
                setError(formatApiError(msg || detail) || "Assignment failed.");
            }
        } finally { setBusy(false); }
    }

    return (
        <section data-testid="attn-detail-ownership" className="border border-[#E5E7EB] rounded-md p-3 bg-[#F8FAFC]">
            <div className="text-[11px] uppercase tracking-wide text-[#94A3B8]">Ownership</div>
            <div className="mt-1 text-[13px] text-[#0F172A]" data-testid="attn-detail-current-owner">
                {hasOwner
                    ? <>Owned by <strong>{currentOwner}</strong>{row.assigned_role ? ` (${row.assigned_role})` : ""}</>
                    : <span className="text-[#4B5565]">Unassigned</span>}
            </div>
            {row.assigned_at && (
                <div className="text-[11px] text-[#94A3B8]" data-testid="attn-detail-assigned-at">
                    assigned {row.assigned_at.slice(0, 16).replace("T", " ")}
                    {row.assigned_by_name ? ` by ${row.assigned_by_name}` : ""}
                </div>
            )}
            {row.assignment_reason && (
                <div className="mt-1 text-[11px] text-[#4B5565]" data-testid="attn-assignment-reason">
                    Last reason: {row.assignment_reason}
                </div>
            )}
            {!readOnly && (
                <>
                    <div className="mt-2 flex gap-2 text-[12px]">
                        <button
                            data-testid="attn-mode-user"
                            onClick={() => setMode("user")}
                            className={`px-2 py-1 rounded-md border ${mode === "user" ? "bg-white border-[#0F172A]" : "border-[#E5E7EB]"}`}
                        >User</button>
                        <button
                            data-testid="attn-mode-role"
                            onClick={() => setMode("role")}
                            className={`px-2 py-1 rounded-md border ${mode === "role" ? "bg-white border-[#0F172A]" : "border-[#E5E7EB]"}`}
                        >Role</button>
                    </div>
                    {mode === "user" && (
                        <label className="block mt-2 text-[12px] text-[#4B5565]">
                            <span className="sr-only">User</span>
                            <select
                                data-testid="attn-assign-user-select"
                                value={userId}
                                onChange={(e) => setUserId(e.target.value)}
                                className="w-full border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                            >
                                <option value="">Select user…</option>
                                {assignees.users.map((u) => (
                                    <option key={u.id} value={u.id}>{u.name} ({u.role})</option>
                                ))}
                            </select>
                        </label>
                    )}
                    {mode === "role" && (
                        <label className="block mt-2 text-[12px] text-[#4B5565]">
                            <span className="sr-only">Role</span>
                            <select
                                data-testid="attn-assign-role-select"
                                value={role}
                                onChange={(e) => setRole(e.target.value)}
                                className="w-full border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                            >
                                <option value="">Select role…</option>
                                {assignees.roles.map((r) => (
                                    <option key={r} value={r}>{r}</option>
                                ))}
                            </select>
                        </label>
                    )}
                    <label className="block mt-2 text-[12px] text-[#4B5565]">
                        <span className="sr-only">Reason</span>
                        <input
                            data-testid="attn-assign-reason"
                            placeholder={needsReason ? "Reason (required)" : "Reason (optional)"}
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            className="w-full border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                        />
                    </label>
                    {error && <div data-testid="attn-assign-error" className="mt-2 text-[12px] text-[#DC2626]">{error}</div>}
                    <div className="mt-2 flex flex-wrap gap-2">
                        <button
                            data-testid="attn-assign-submit"
                            disabled={busy || (mode === "user" ? !userId : !role)}
                            onClick={() => submit(mode)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-[#0F172A] text-white text-[12px] disabled:opacity-50"
                        >
                            {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                            {hasOwner ? "Reassign" : "Assign"}
                        </button>
                        <button
                            data-testid="attn-assign-me"
                            disabled={busy}
                            onClick={() => submit("self")}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#0F172A] text-[#0F172A] text-[12px] hover:bg-white disabled:opacity-50"
                        >
                            {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                            Assign to me
                        </button>
                    </div>
                </>
            )}
            {readOnly && (
                <div data-testid="attn-ownership-readonly"
                     className="mt-2 text-[11px] text-[#94A3B8] italic">
                    Read-only history — this exception is resolved.
                </div>
            )}
        </section>
    );
}

function AcknowledgementCard({ row, onMutated, readOnly = false }) {
    const [note, setNote] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const acknowledged = Boolean(row.acknowledged_at);

    async function submit() {
        setError(null); setBusy(true);
        try {
            await api.operationsAttentionAcknowledge(row.id, { note: note.trim() || undefined });
            await onMutated();
            setNote("");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = detail && detail.error_code;
            if (code === "exception_resolved") {
                setError("This exception is already resolved — acknowledgement is disabled.");
            } else {
                setError(formatApiError(detail?.detail || detail) || "Acknowledge failed.");
            }
        } finally { setBusy(false); }
    }

    return (
        <section data-testid="attn-detail-ack" className="border border-[#E5E7EB] rounded-md p-3">
            <div className="text-[11px] uppercase tracking-wide text-[#94A3B8]">Acknowledgement</div>
            {acknowledged ? (
                <div className="mt-1 text-[13px] text-[#0F172A]" data-testid="attn-detail-ack-status">
                    Acknowledged by <strong>{row.acknowledged_by_name || "Operations"}</strong>
                    {" "}on {row.acknowledged_at?.slice(0, 16).replace("T", " ")}
                    {row.acknowledgement_note ? ` — ${row.acknowledgement_note}` : ""}
                </div>
            ) : (
                <div className="mt-1 text-[12px] text-[#B45309]" data-testid="attn-detail-ack-status">
                    Not yet acknowledged.
                </div>
            )}
            {!readOnly && (
                <>
                    <label className="block mt-2 text-[12px] text-[#4B5565]">
                        <span className="sr-only">Note</span>
                        <input
                            data-testid="attn-ack-note"
                            placeholder="Optional note"
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            className="w-full border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                        />
                    </label>
                    {error && <div data-testid="attn-ack-error" className="mt-2 text-[12px] text-[#DC2626]">{error}</div>}
                    <button
                        data-testid="attn-ack-submit"
                        disabled={busy}
                        onClick={submit}
                        className="mt-2 inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#0F172A] text-[#0F172A] text-[12px] hover:bg-[#F8FAFC] disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                        {acknowledged ? "Re-acknowledge" : "Acknowledge"}
                    </button>
                </>
            )}
            {readOnly && (
                <div data-testid="attn-ack-readonly"
                     className="mt-2 text-[11px] text-[#94A3B8] italic">
                    Read-only history — this exception is resolved.
                </div>
            )}
        </section>
    );
}

function NotesSection({ row, onMutated, readOnly = false }) {
    const [content, setContent] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const notes = (row.operational_notes || []).slice().sort(
        (a, b) => (a.created_at || "").localeCompare(b.created_at || ""),
    );
    const currentOccurrence = row.occurrence_count || 1;

    async function submit() {
        setError(null);
        if (!content.trim()) { setError("Enter a note first."); return; }
        setBusy(true);
        try {
            await api.operationsAttentionAddNote(row.id, content);
            await onMutated();
            setContent("");
        } catch (e) {
            const detail = e?.response?.data?.detail;
            const code = detail && detail.error_code;
            if (code === "exception_resolved") {
                setError("This exception is already resolved — notes are disabled.");
            } else {
                setError(formatApiError(detail?.detail || detail) || "Failed to add note.");
            }
        } finally { setBusy(false); }
    }

    return (
        <section data-testid="attn-detail-notes" className="border border-[#E5E7EB] rounded-md p-3">
            <div className="flex items-center gap-2">
                <div className="text-[11px] uppercase tracking-wide text-[#94A3B8] flex-1">Operational notes</div>
                <span className="text-[11px] text-[#94A3B8]" data-testid="attn-detail-notes-count">{notes.length}</span>
            </div>
            {notes.length === 0 && (
                <div className="mt-1 text-[12px] text-[#94A3B8]" data-testid="attn-detail-notes-empty">No notes yet.</div>
            )}
            <ul className="mt-2 space-y-2" data-testid="attn-detail-notes-list">
                {notes.map((n) => {
                    const isPrior = n.occurrence_count && n.occurrence_count < currentOccurrence;
                    return (
                        <li key={n.id}
                            data-testid={`attn-detail-note-${n.id}`}
                            className={`text-[12px] p-2 rounded-md border ${isPrior ? "border-dashed border-[#94A3B8]/40 bg-[#F1F5F9]" : "border-[#E5E7EB] bg-white"}`}>
                            <div className="flex items-center gap-2 text-[11px] text-[#4B5565]">
                                <span data-testid={`attn-detail-note-${n.id}-author`}>{n.author_name || "Operations"}</span>
                                <span>·</span>
                                <span>{n.created_at?.slice(0, 16).replace("T", " ")}</span>
                                {isPrior && (
                                    <span className="ml-auto px-1.5 py-0.5 rounded-full text-[10px] border border-[#94A3B8]/30 bg-white text-[#4B5565]"
                                          data-testid={`attn-detail-note-${n.id}-prior`}>
                                        Occurrence #{n.occurrence_count}
                                    </span>
                                )}
                            </div>
                            <div className="mt-1 text-[#0F172A] whitespace-pre-wrap">{n.content}</div>
                        </li>
                    );
                })}
            </ul>
            {!readOnly && (
                <>
                    <label className="block mt-2 text-[12px] text-[#4B5565]">
                        <span className="sr-only">New note</span>
                        <textarea
                            data-testid="attn-note-input"
                            placeholder="Add a note (visible to Operations only)"
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            rows={2}
                            maxLength={2000}
                            className="w-full border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                        />
                    </label>
                    {error && <div data-testid="attn-note-error" className="mt-1 text-[12px] text-[#DC2626]">{error}</div>}
                    <button
                        data-testid="attn-note-submit"
                        disabled={busy}
                        onClick={submit}
                        className="mt-1 inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#0F172A] text-[#0F172A] text-[12px] hover:bg-[#F8FAFC] disabled:opacity-50"
                    >
                        {busy ? <Loader2 size={12} className="animate-spin" /> : null}
                        Add note
                    </button>
                </>
            )}
            {readOnly && (
                <div data-testid="attn-notes-readonly"
                     className="mt-2 text-[11px] text-[#94A3B8] italic">
                    Read-only history — this exception is resolved.
                </div>
            )}
        </section>
    );
}



const _HISTORY_ACTION_LABELS = {
    opened:       "Opened",
    reopened:     "Reopened",
    resolved:     "Resolved",
    escalated:    "Escalated",
    reassigned:   "Auto-reassigned",
    assigned:     "Assigned",
    acknowledged: "Acknowledged",
    note_added:   "Note added",
};

function ActionHistorySection({ row }) {
    const history = (row.action_history || []).slice().sort(
        (a, b) => (b.timestamp || "").localeCompare(a.timestamp || ""),  // newest first
    );
    if (history.length === 0) {
        return (
            <section data-testid="attn-detail-history" className="border border-[#E5E7EB] rounded-md p-3">
                <div className="text-[11px] uppercase tracking-wide text-[#94A3B8]">Action history</div>
                <div className="mt-1 text-[12px] text-[#94A3B8]">No activity yet.</div>
            </section>
        );
    }
    return (
        <section data-testid="attn-detail-history" className="border border-[#E5E7EB] rounded-md p-3">
            <div className="text-[11px] uppercase tracking-wide text-[#94A3B8]">Action history</div>
            <ol className="mt-2 space-y-2">
                {history.map((h) => (
                    <li key={h.id} data-testid={`attn-detail-history-${h.id}`}
                        className="text-[12px] border-l-2 border-[#E5E7EB] pl-3">
                        <div className="flex items-center gap-2 text-[11px] text-[#94A3B8]">
                            <span data-testid={`attn-detail-history-${h.id}-timestamp`}>
                                {h.timestamp?.slice(0, 16).replace("T", " ")}
                            </span>
                            <span>·</span>
                            <span data-testid={`attn-detail-history-${h.id}-actor`}>
                                {h.actor?.name || "System"}
                                {h.actor?.role && h.actor.role !== "system" ? ` (${h.actor.role})` : ""}
                            </span>
                            {h.occurrence_count && h.occurrence_count > 1 && (
                                <>
                                    <span>·</span>
                                    <span>occurrence #{h.occurrence_count}</span>
                                </>
                            )}
                        </div>
                        <div className="mt-0.5 text-[#0F172A]" data-testid={`attn-detail-history-${h.id}-action`}>
                            {_HISTORY_ACTION_LABELS[h.action] || h.action}
                            {h.action === "escalated" && h.metadata?.escalation_level ? ` — L${h.metadata.escalation_level}` : ""}
                            {h.action === "assigned" && h.metadata?.to
                                ? ` — ${h.metadata.to.assigned_user_name || h.metadata.to.assigned_role || "unassigned"}`
                                : ""}
                        </div>
                        {h.reason && (
                            <div className="mt-0.5 text-[11px] text-[#4B5565]" data-testid={`attn-detail-history-${h.id}-reason`}>
                                Reason: {h.reason}
                            </div>
                        )}
                        {h.note && (
                            <div className="mt-0.5 text-[11px] text-[#4B5565] whitespace-pre-wrap">“{h.note}”</div>
                        )}
                    </li>
                ))}
            </ol>
        </section>
    );
}

function Meta({ k, v }) {
    return (
        <div>
            <div className="text-[11px] uppercase tracking-wide text-[#94A3B8]">{k}</div>
            <div className="mt-0.5 text-[13px] text-[#0F172A] break-words">{v ?? "—"}</div>
        </div>
    );
}

function Filters({ current, onChange, onReset, options }) {
    // Visual "effective" values so the Status dropdown shows "active"
    // by default (matching the server default) instead of a blank slot.
    const eff = effectiveFilters(current);
    const ruleOptions = options?.rules || [];
    const projectOptions = options?.projects || [];
    const customerOptions = options?.customers || [];
    const userOptions = options?.users || [];
    return (
        <div data-testid="attn-filters" className="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-[#E5E7EB] bg-white">
            <FilterIcon size={14} className="text-[#4B5565]" />
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Status</span>
                <select
                    data-testid="attn-filter-status"
                    value={eff.status}
                    onChange={(e) => onChange("status", e.target.value || null)}
                    className="ml-1 border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                >
                    {[...VALID_STATUS].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
            </label>
            {[
                ["severity", VALID_SEVERITY, "Severity"],
                ["effect", VALID_EFFECT, "Effect"],
                ["rule_category", VALID_CATEGORY, "Category"],
            ].map(([key, allowed, label]) => (
                <label key={key} className="text-[12px] text-[#4B5565]">
                    <span className="sr-only">{label}</span>
                    <select
                        data-testid={`attn-filter-${key}`}
                        value={current[key] || ""}
                        onChange={(e) => onChange(key, e.target.value || null)}
                        className="ml-1 border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                    >
                        <option value="">{label}</option>
                        {[...allowed].map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                </label>
            ))}
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Sort</span>
                <select
                    data-testid="attn-filter-sort"
                    value={eff.sort}
                    onChange={(e) => onChange("sort", e.target.value || null)}
                    className="ml-1 border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                >
                    {[...VALID_SORT].map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
            </label>
            {/* Rule (rule_key) — searchable via datalist. */}
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Rule</span>
                <input
                    data-testid="attn-filter-rule"
                    list="attn-rule-options"
                    placeholder="Rule"
                    value={current.rule_key || ""}
                    onChange={(e) => onChange("rule_key", e.target.value.trim() || null)}
                    className="border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px] w-44"
                    aria-label="Rule"
                />
                <datalist id="attn-rule-options">
                    {ruleOptions.map((r) => (
                        <option key={r.key} value={r.key}>{r.name}</option>
                    ))}
                </datalist>
            </label>
            {/* Role (responsible_role). */}
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Role</span>
                <input
                    data-testid="attn-filter-role"
                    placeholder="Role"
                    value={current.responsible_role || ""}
                    onChange={(e) => onChange("responsible_role", e.target.value.trim() || null)}
                    className="border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px] w-24"
                    aria-label="Responsible role"
                />
            </label>
            {/* Responsible person (responsible_user_id) — human labels via datalist. */}
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Person</span>
                <select
                    data-testid="attn-filter-user"
                    value={current.responsible_user_id || ""}
                    onChange={(e) => onChange("responsible_user_id", e.target.value || null)}
                    className="ml-1 border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px] w-36"
                >
                    <option value="">Person</option>
                    {userOptions.map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                </select>
            </label>
            {/* Customer (customer_id) — human labels. */}
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Customer</span>
                <select
                    data-testid="attn-filter-customer"
                    value={current.customer_id || ""}
                    onChange={(e) => onChange("customer_id", e.target.value || null)}
                    className="ml-1 border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px] w-40"
                >
                    <option value="">Customer</option>
                    {customerOptions.map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                </select>
            </label>
            {/* Project (project_id) — human labels, never raw UUID for selection. */}
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Project</span>
                <select
                    data-testid="attn-filter-project"
                    value={current.project_id || ""}
                    onChange={(e) => onChange("project_id", e.target.value || null)}
                    className="ml-1 border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px] w-52"
                >
                    <option value="">Project</option>
                    {projectOptions.map((p) => (
                        <option key={p.id} value={p.id}>{p.label}</option>
                    ))}
                </select>
            </label>
            <input
                data-testid="attn-filter-age"
                type="number"
                placeholder="Min age (h)"
                value={current.min_age_hours || ""}
                onChange={(e) => onChange("min_age_hours", e.target.value || null)}
                className="border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px] w-24"
                aria-label="Minimum age in hours"
            />
            {/* OPS-003B · Assignment status */}
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Assignment</span>
                <select
                    data-testid="attn-filter-assignment-status"
                    value={current.assignment_status || ""}
                    onChange={(e) => onChange("assignment_status", e.target.value || null)}
                    className="ml-1 border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                >
                    <option value="">Assignment</option>
                    <option value="assigned">Assigned</option>
                    <option value="unassigned">Unassigned</option>
                </select>
            </label>
            {/* OPS-003B · Acknowledgement status */}
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Acknowledgement</span>
                <select
                    data-testid="attn-filter-ack-status"
                    value={current.acknowledgement_status || ""}
                    onChange={(e) => onChange("acknowledgement_status", e.target.value || null)}
                    className="ml-1 border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                >
                    <option value="">Acknowledgement</option>
                    <option value="acknowledged">Acknowledged</option>
                    <option value="unacknowledged">Unacknowledged</option>
                </select>
            </label>
            {/* OPS-003B · Assigned user (from the live assignee directory) */}
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Assigned user</span>
                <select
                    data-testid="attn-filter-assigned-user"
                    value={current.assigned_user_id || ""}
                    onChange={(e) => onChange("assigned_user_id", e.target.value || null)}
                    className="ml-1 border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px] w-36"
                >
                    <option value="">Assigned to</option>
                    {(options?.assignees || []).map((u) => (
                        <option key={u.id} value={u.id}>{u.name}</option>
                    ))}
                </select>
            </label>
            {/* OPS-003B · Assigned role */}
            <label className="text-[12px] text-[#4B5565]">
                <span className="sr-only">Assigned role</span>
                <select
                    data-testid="attn-filter-assigned-role"
                    value={current.assigned_role || ""}
                    onChange={(e) => onChange("assigned_role", e.target.value || null)}
                    className="ml-1 border border-[#E5E7EB] rounded-md px-2 py-1 text-[12px]"
                >
                    <option value="">Role</option>
                    {(options?.assignableRoles || []).map((r) => (
                        <option key={r} value={r}>{r}</option>
                    ))}
                </select>
            </label>
            <button
                data-testid="attn-reset"
                onClick={onReset}
                className="text-[12px] text-[#4B5565] hover:text-[#0F172A] underline ml-auto"
            >Reset</button>
        </div>
    );
}

export default function OperationsAttentionCentre() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [summary, setSummary] = useState(null);
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [offset, setOffset] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [lastRefreshedAt, setLastRefreshedAt] = useState(null);
    // Filter option rosters (rules from registry + derived project /
    // customer / user labels from a lightweight all-status roster fetch).
    const [rulesRegistry, setRulesRegistry] = useState([]);
    const [roster, setRoster] = useState([]);
    // OPS-003B · assignable directory drives the Assigned-to filter
    // AND the DetailPanel's assign form. Loaded once + refreshed on
    // manual refresh.
    const [assignees, setAssignees] = useState({ users: [], roles: [] });

    const filters = useMemo(() => readFilters(searchParams), [searchParams]);
    const effective = useMemo(() => effectiveFilters(filters), [filters]);
    const selectedId = searchParams.get("open") || null;
    // OPS-DASHBOARD-DEEP-LINKS · Dashboard hero cards for Critical /
    // High Attention arrive with `auto_open=first`. This is a
    // one-shot hint: the first successful list load resolves it to a
    // concrete `open=<exception_id>` (or clears it on zero results)
    // and REMOVES the marker so a refresh reproduces the same
    // exception and never re-picks a different first row. An
    // explicit valid `open=<id>` always wins over the marker.
    const autoOpenHint = searchParams.get("auto_open");

    const load = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const [sum, list] = await Promise.all([
                api.operationsExceptionsSummary(),
                api.operationsExceptionsSearch({ ...effective, limit: PAGE_SIZE, offset }),
            ]);
            setSummary(sum);
            setRows(list.exceptions);
            setTotal(list.total);
            setLastRefreshedAt(new Date());
        } catch (e) {
            setError(formatApiError(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    }, [effective, offset]);

    useEffect(() => { load(); }, [load]);

    // OPS-DASHBOARD-DEEP-LINKS · Resolve `auto_open=first` to a
    // concrete `open=<exception_id>` once the list has loaded.
    //
    //   • Explicit `open=<id>` takes precedence — the marker is
    //     simply consumed so refresh/share stays stable.
    //   • Zero rows → strip the marker without setting `open` so the
    //     empty state stays visible.
    //   • Otherwise → set `open=<first.id>` (rows come back sorted
    //     `severity_desc` then `age_desc` server-side, so the first
    //     row is deterministic under identical filters).
    //
    // The list request is a GET (`operationsExceptionsSearch`) and
    // `operationsExceptionDetail` is a GET too — the auto-open path
    // therefore performs ZERO mutations against the exception.
    useEffect(() => {
        if (loading) return;
        if (autoOpenHint !== "first") return;
        const currentOpen = searchParams.get("open");
        const p = new URLSearchParams(searchParams);
        p.delete("auto_open");
        if (currentOpen) {
            setSearchParams(p, { replace: true });
            return;
        }
        if (rows.length === 0) {
            setSearchParams(p, { replace: true });
            return;
        }
        p.set("open", rows[0].id);
        setSearchParams(p, { replace: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loading, autoOpenHint, rows]);

    // One-shot fetch of the rules registry + a broader "all-status"
    // roster for the filter dropdowns. Silent-fail: filters degrade to
    // showing whatever is on the current page instead of blocking.
    useEffect(() => {
        api.operationsRulesRegistry().then((r) => setRulesRegistry(r?.rules || [])).catch(() => {});
        api.operationsExceptionsSearch({ status: "all", limit: 500 })
            .then((r) => setRoster(r?.exceptions || [])).catch(() => {});
        // OPS-003B · assignable directory bootstrap.
        api.operationsAttentionAssignees()
            .then((r) => setAssignees({ users: r?.users || [], roles: r?.roles || [] }))
            .catch(() => {});
    }, []);

    // Refresh roster whenever the user manually refreshes so newly
    // seen customers / projects appear in the dropdowns without a
    // page reload.
    const refresh = useCallback(() => {
        load();
        api.operationsExceptionsSearch({ status: "all", limit: 500 })
            .then((r) => setRoster(r?.exceptions || [])).catch(() => {});
        api.operationsAttentionAssignees()
            .then((r) => setAssignees({ users: r?.users || [], roles: r?.roles || [] }))
            .catch(() => {});
    }, [load]);

    // Conservative auto-refresh: every 60s, only when tab is visible.
    useEffect(() => {
        const t = setInterval(() => {
            if (!document.hidden) load();
        }, 60_000);
        return () => clearInterval(t);
    }, [load]);

    const filterOptions = useMemo(() => {
        // Merge current visible rows with the broader roster so
        // dropdown options remain populated after filtering.
        const seen = new Map();
        const uniqBy = (arr, keyFn, projFn) => {
            for (const item of arr) {
                const k = keyFn(item); if (!k) continue;
                if (seen.has(k)) continue;
                seen.set(k, projFn(item));
            }
        };
        // Projects — from roster and current rows; primary label uses
        // the canonical fallback chain (never the raw UUID).
        const projs = new Map();
        for (const r of [...roster, ...rows]) {
            const pid = r.project_id;
            if (!pid || projs.has(pid)) continue;
            projs.set(pid, {
                id: pid,
                label: projectDisplay(r),
            });
        }
        // Customers — orphaned customer_ids (deleted user rows) are
        // labelled neutrally, never as a shortened UUID.
        const custs = new Map();
        for (const r of [...roster, ...rows]) {
            const cid = r.customer_id;
            if (!cid || custs.has(cid)) continue;
            custs.set(cid, {
                id: cid,
                name: r.customer_name || "Former or unavailable customer",
            });
        }
        // Responsible persons — same rule. An orphaned responsible_user_id
        // remains a legitimate filter target (still gates on the id, so
        // triage of stale exceptions is possible) but must not be
        // presented under a fabricated "User abc123" alias.
        const users = new Map();
        for (const r of [...roster, ...rows]) {
            const uid = r.responsible_user_id;
            if (!uid || users.has(uid)) continue;
            users.set(uid, {
                id: uid,
                name: r.responsible_user_name || "Former or unavailable user",
            });
        }
        return {
            rules: rulesRegistry.map((r) => ({ key: r.key, name: r.name })),
            projects: [...projs.values()].sort((a, b) => a.label.localeCompare(b.label)),
            customers: [...custs.values()].sort((a, b) => a.name.localeCompare(b.name)),
            users: [...users.values()].sort((a, b) => a.name.localeCompare(b.name)),
            // OPS-003B · dedicated assignable directory feeds the new
            // "Assigned to" filter. Distinct from the responsible-user
            // dropdown above so the two filters don't get confused.
            assignees: assignees.users,
            assignableRoles: assignees.roles,
        };
    }, [roster, rows, rulesRegistry, assignees]);

    // Every URL mutation uses REPLACE so browser history walks back to
    // whichever page brought the operator here — not through their
    // Attention Centre journey. This holds for filters, paging, reset
    // AND detail open/close.
    const setFilter = (key, value) => {
        const p = new URLSearchParams(searchParams);
        if (value == null || value === "") p.delete(key); else p.set(key, String(value));
        p.delete("open");
        // Filter change is a fresh navigation intent — drop the
        // one-shot auto-open marker so the next list doesn't
        // auto-select an unrelated row.
        p.delete("auto_open");
        setSearchParams(p, { replace: true });
        setOffset(0);
    };
    const resetFilters = () => {
        const p = new URLSearchParams(searchParams);
        CANONICAL_FILTERS.forEach((k) => p.delete(k));
        p.delete("auto_open");
        setSearchParams(p, { replace: true });
        setOffset(0);
    };
    const openDetail = (id) => {
        const p = new URLSearchParams(searchParams);
        p.set("open", id);
        p.delete("auto_open");
        setSearchParams(p, { replace: true });
    };
    const closeDetail = () => {
        const p = new URLSearchParams(searchParams);
        p.delete("open");
        p.delete("auto_open");
        setSearchParams(p, { replace: true });
    };

    const critical = summary?.by_severity?.find((b) => b.key === "critical")?.count || 0;
    const high     = summary?.by_severity?.find((b) => b.key === "high")?.count || 0;
    const mine     = summary?.assigned_to_me ?? 0;

    return (
        <OperationsShell>
            <div data-testid="operations-attention-centre" className="min-h-screen bg-[#F5F5F7] flex flex-col">
                {/* DEV-022 · Attention Centre premium hero — makes
                    the exception load and oldest-breach severity
                    unambiguous the moment the page opens. Replaces
                    the previous plain white header row. */}
                <div className="px-4 sm:px-6 pt-6">
                    {(() => {
                        // Attention Centre is permission-gated to
                        // Operations only, so the hero always shows
                        // the Operations identity.
                        const identity = portalIdentity("operations");
                        const active = summary?.active_total ?? 0;
                        const sentence = loading && !summary ? (
                            <>Loading current exceptions…</>
                        ) : error ? (
                            <>Exception summary unavailable — retry below.</>
                        ) : active === 0 ? (
                            <>All clear — no active exceptions require your attention.</>
                        ) : (
                            <>
                                You have <strong className="text-white font-semibold">{active}</strong> active {active === 1 ? "exception" : "exceptions"}
                                {critical > 0 && <> · <span className="text-[#FCA5A5]">{critical} critical</span></>}
                                {high > 0 && <> · <span className="text-[#FCD34D]">{high} high</span></>}
                                {mine > 0 && <> · <span className="text-white">{mine} assigned to you</span></>}
                            </>
                        );
                        return (
                            <PortalHero
                                testid="attn-hero"
                                eyebrow={`${identity.eyebrow} · ATTENTION CENTRE`}
                                title="What needs attention now"
                                sentence={sentence}
                                cta={{
                                    to: "/operations",
                                    label: "Back to Operations",
                                    icon: Home,
                                }}
                                ring={{
                                    value: loading && !summary ? "—" : active,
                                    label: active === 1 ? "active" : "active",
                                }}
                                tag={lastRefreshedAt ? `Refreshed ${lastRefreshedAt.toLocaleTimeString()}` : "Live rule engine"}
                            />
                        );
                    })()}
                </div>

                {/* Compact utility row — refresh + home for keyboard/AT users */}
                <div className="px-4 sm:px-6 py-3 flex items-center gap-3">
                    <button
                        data-testid="attn-home"
                        onClick={() => navigate("/operations")}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC] text-[13px]"
                        aria-label="Return to Operations dashboard"
                    >
                        <Home size={14} strokeWidth={1.75} /> Home
                    </button>
                    <div className="flex items-center gap-2 ml-auto text-[12px] text-[#4B5565]">
                        {lastRefreshedAt && <span data-testid="attn-last-refreshed">Refreshed {lastRefreshedAt.toLocaleTimeString()}</span>}
                        <button
                            data-testid="attn-refresh"
                            onClick={refresh}
                            disabled={loading}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-[#E5E7EB] bg-white text-[#0F172A] hover:bg-[#F8FAFC] disabled:opacity-50"
                        >
                            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Refresh
                        </button>
                    </div>
                </div>

                {/* Blocking tile intentionally removed — see BLOCKING_TILE_REMOVAL
                    comment at top of file. Blocking effect is a transient 409
                    decision, never a persisted active exception. */}
                <section data-testid="attn-summary" className="grid grid-cols-2 sm:grid-cols-4 gap-3 px-4 sm:px-6 py-4">
                    <SummaryTile testid="attn-summary-total" label="Active total" value={summary?.active_total ?? 0} />
                    <SummaryTile testid="attn-summary-critical" label="Critical" value={critical} tone="critical" />
                    <SummaryTile testid="attn-summary-high" label="High" value={high} tone="high" />
                    <SummaryTile testid="attn-summary-mine" label="Assigned to me" value={mine} />
                </section>

                <Filters current={filters} onChange={setFilter} onReset={resetFilters} options={filterOptions} />

                <div className="flex-1 flex min-h-0">
                    <div className="flex-1 min-w-0 bg-white border-t border-[#E5E7EB]">
                        {loading && rows.length === 0 && (
                            <div data-testid="attn-loading" className="p-6 text-sm text-[#4B5565] flex items-center gap-2">
                                <Loader2 className="animate-spin" size={16} /> Loading exceptions…
                            </div>
                        )}
                        {error && !loading && (
                            <div data-testid="attn-error" className="p-6 text-sm text-[#DC2626]">
                                {error} <button onClick={load} className="underline ml-2">Retry</button>
                            </div>
                        )}
                        {!loading && !error && rows.length === 0 && (
                            <div data-testid="attn-empty" className="p-6 text-sm text-[#4B5565]">
                                {Object.values(filters).some((v) => v) ? "No exceptions match the current filters." : "No active exceptions. All clear ✓"}
                            </div>
                        )}
                        <ul className="divide-y divide-transparent" aria-label="Exception list">
                            {rows.map((r) => (
                                <li key={r.id}>
                                    <ExceptionRow row={r} onOpen={openDetail} selected={r.id === selectedId} />
                                </li>
                            ))}
                        </ul>
                        {total > PAGE_SIZE && (
                            <div className="flex items-center justify-between px-4 py-3 text-[12px] text-[#4B5565]">
                                <span>{offset + 1}–{Math.min(offset + rows.length, total)} of {total}</span>
                                <div className="flex gap-2">
                                    <button data-testid="attn-prev" disabled={offset === 0}
                                        onClick={() => setOffset(Math.max(0, offset - PAGE_SIZE))}
                                        className="px-2 py-1 border border-[#E5E7EB] rounded-md disabled:opacity-40">Previous</button>
                                    <button data-testid="attn-next" disabled={offset + PAGE_SIZE >= total}
                                        onClick={() => setOffset(offset + PAGE_SIZE)}
                                        className="px-2 py-1 border border-[#E5E7EB] rounded-md disabled:opacity-40">Next</button>
                                </div>
                            </div>
                        )}
                    </div>
                    {selectedId && <DetailPanel exceptionId={selectedId} onClose={closeDetail} onMutated={refresh} />}
                </div>
            </div>
        </OperationsShell>
    );
}

function SummaryTile({ label, value, tone, testid }) {
    const map = {
        critical: "border-[#DC2626]/25 bg-[#DC2626]/[0.04]",
        high: "border-[#F59E0B]/25 bg-[#F59E0B]/[0.04]",
    };
    return (
        <div data-testid={testid}
             className={`rounded-lg p-3 border ${map[tone] || "border-[#E5E7EB] bg-white"}`}>
            <div className="text-[11px] uppercase tracking-wide text-[#4B5565]">{label}</div>
            <div className="mt-1 text-2xl font-semibold text-[#0F172A]">{value}</div>
        </div>
    );
}
